import type { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import Sentry from "@similarweb/sentry-logger";

import { RANK_TRACKER_DEMO_CAMPAIGNS_CONFIG_KEY } from "app/common/campaigns/KeywordsCampaignConsts";
import {
    enrichCampaignsWithIcons,
    withCampaignDatesUtc,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsContextUtils";
import type {
    IKeywordsCampaignData,
    IKeywordsCampaignsCampaignRawData,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { RankTrackerQueryType } from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import { createRankTrackerQueryKey } from "app/pages/digital-marketing/keywords-campaigns/shared/utils";
import { DEMO_CAMPAIGNS_URL } from "app/pages/digital-marketing/keywords-campaigns/wizard/constants";
import { DefaultFetchService } from "app/services/fetchService";

import { useRankTrackerQuery } from "../useRankTrackerQuery";

const fetchService = DefaultFetchService.getInstance();

export const fetchDemoCampaigns = async (): Promise<IKeywordsCampaignData[]> => {
    try {
        const campaigns: IKeywordsCampaignsCampaignRawData[] = await fetchService.get(
            DEMO_CAMPAIGNS_URL,
            {},
            {
                preventAutoCancellation: true, //requires to prevent auto cancellation on navigation change during the fetch
            },
        );
        const managedCampaigns = campaigns.map((campaign) => ({
            ...withCampaignDatesUtc(campaign),
            isDemo: true,
        }));
        const enrichedCampaigns: IKeywordsCampaignData[] = await enrichCampaignsWithIcons(
            managedCampaigns as IKeywordsCampaignData[],
        );
        localStorage.setItem(RANK_TRACKER_DEMO_CAMPAIGNS_CONFIG_KEY, JSON.stringify(enrichedCampaigns));
        return enrichedCampaigns;
    } catch (error) {
        Sentry.error(`Error fetching demo campaigns:`, error as Error);
        return [];
    }
};

type QueryOptions = Omit<UseQueryOptions<IKeywordsCampaignData[]>, "queryKey" | "queryFn">;

const DEMO_CAMPAIGNS_QUERY_KEY: QueryKey = createRankTrackerQueryKey(RankTrackerQueryType.DemoCampaigns);

export const useDemoCampaignsQuery = (options?: QueryOptions) => {
    return useRankTrackerQuery({
        queryKey: DEMO_CAMPAIGNS_QUERY_KEY,
        queryFn: () => fetchDemoCampaigns(),
        throwOnError: (error) => {
            Sentry.errorTracker({
                e: error,
                level: "error",
                message: "Error fetching demo campaigns configurations",
            });
            return false;
        },
        ...options,
    });
};
